// SmartSearchDropdown - Recherche intelligente niveau élite pour Artisans
// Desktop/Tablette uniquement - Auto-suggestions + Résultats directs + Tri intelligent

import { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, MapPin, Star, Clock, Zap, TrendingUp, 
  Shield, Eye, Award, ChevronRight, SlidersHorizontal,
  Sparkles, Users, Wrench, X
} from 'lucide-react';

import { API_URL } from '../../config/api';

// Mapping intentions → métiers
const INTENT_MAPPING = {
  // Problèmes courants
  'fuite': ['Plombier'],
  'fuite d\'eau': ['Plombier'],
  'chauffe-eau': ['Plombier', 'Chauffagiste'],
  'wc': ['Plombier'],
  'toilettes': ['Plombier'],
  'bouchées': ['Plombier'],
  'canalisation': ['Plombier'],
  'robinet': ['Plombier'],
  
  'panne électrique': ['Électricien'],
  'disjoncteur': ['Électricien'],
  'prise': ['Électricien'],
  'lumière': ['Électricien'],
  'tableau électrique': ['Électricien'],
  
  'chauffage': ['Chauffagiste', 'Plombier'],
  'radiateur': ['Chauffagiste', 'Plombier'],
  'chaudière': ['Chauffagiste'],
  'pompe à chaleur': ['Chauffagiste'],
  'climatisation': ['Chauffagiste'],
  
  'toiture': ['Couvreur'],
  'toit': ['Couvreur'],
  'tuile': ['Couvreur'],
  'gouttière': ['Couvreur'],
  'infiltration': ['Couvreur'],
  
  'peinture': ['Peintre'],
  'papier peint': ['Peintre'],
  'mur': ['Peintre', 'Maçon'],
  
  'carrelage': ['Carreleur'],
  'faïence': ['Carreleur'],
  'salle de bain': ['Carreleur', 'Plombier'],
  
  'meuble': ['Menuisier'],
  'porte': ['Menuisier'],
  'fenêtre': ['Menuisier'],
  'parquet': ['Menuisier'],
  'cuisine': ['Menuisier'],
  
  'piscine': ['Pisciniste'],
  'bassin': ['Pisciniste'],
  
  'jardin': ['Paysagiste'],
  'terrasse': ['Paysagiste', 'Maçon'],
  'clôture': ['Paysagiste'],
  
  'rénovation': ['Maçon', 'Peintre', 'Électricien', 'Plombier'],
  'construction': ['Maçon'],
  'extension': ['Maçon'],
  'fondation': ['Maçon'],
  
  // Urgences
  'urgence': ['Plombier', 'Électricien', 'Couvreur'],
  'dépannage': ['Plombier', 'Électricien', 'Chauffagiste'],
  'rapide': ['Plombier', 'Électricien'],
};

// Suggestions populaires par défaut
const POPULAR_SEARCHES = [
  { text: 'Plombier urgence', icon: Zap, color: '#EF4444' },
  { text: 'Électricien dépannage', icon: Zap, color: '#F59E0B' },
  { text: 'Peintre rénovation', icon: Wrench, color: '#3B82F6' },
  { text: 'Carreleur salle de bain', icon: Wrench, color: '#8B5CF6' },
];

// Villes principales France
const CITIES = [
  'Paris', 'Lyon', 'Marseille', 'Toulouse', 'Nice', 'Nantes', 
  'Strasbourg', 'Montpellier', 'Bordeaux', 'Lille', 'Rennes'
];

export default function SmartSearchDropdown({ 
  isOpen, 
  onClose, 
  onSearch,
  onOpenFilters,
  artisans = [],
  searchQuery = '',
  setSearchQuery
}) {
  const [localQuery, setLocalQuery] = useState(searchQuery);
  const [suggestions, setSuggestions] = useState([]);
  const [matchedArtisans, setMatchedArtisans] = useState([]);
  const inputRef = useRef(null);
  
  // Focus l'input à l'ouverture
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);
  
  // Sync avec parent
  useEffect(() => {
    setLocalQuery(searchQuery);
  }, [searchQuery]);
  
  // Générer les suggestions intelligentes
  const generateSuggestions = useMemo(() => {
    if (!localQuery || localQuery.length < 2) return [];
    
    const query = localQuery.toLowerCase().trim();
    const results = [];
    
    // 1. Chercher dans le mapping d'intentions
    for (const [intent, metiers] of Object.entries(INTENT_MAPPING)) {
      if (intent.includes(query) || query.includes(intent)) {
        metiers.forEach(metier => {
          if (!results.find(r => r.text === metier && r.type === 'metier')) {
            results.push({
              type: 'metier',
              text: metier,
              subtext: `Résultat pour "${intent}"`,
              icon: Wrench,
              color: '#F97316'
            });
          }
        });
      }
    }
    
    // 2. Suggestions métiers + ville
    const metiers = ['Plombier', 'Électricien', 'Peintre', 'Maçon', 'Carreleur', 
                     'Menuisier', 'Couvreur', 'Chauffagiste', 'Pisciniste', 'Paysagiste',
                     'Architecte', 'Géomètre', 'Expert bâtiment', 'Bureau d\'études', 
                     'Diagnostiqueur', 'Maître d\'œuvre'];
    
    metiers.forEach(metier => {
      const metierLower = metier.toLowerCase();
      if (metierLower.includes(query) || query.includes(metierLower.substring(0, 4))) {
        // Ajouter le métier seul
        if (!results.find(r => r.text === metier && r.type === 'metier')) {
          results.push({
            type: 'metier',
            text: metier,
            subtext: `Tous les ${metier.toLowerCase()}s`,
            icon: Wrench,
            color: '#F97316'
          });
        }
        
        // Ajouter avec intentions populaires
        results.push({
          type: 'intention',
          text: `${metier} urgence`,
          subtext: 'Disponible rapidement',
          icon: Zap,
          color: '#EF4444'
        });
        
        results.push({
          type: 'intention',
          text: `${metier} disponible aujourd'hui`,
          subtext: 'Intervention rapide',
          icon: Clock,
          color: '#10B981'
        });
      }
    });
    
    // 3. Suggestions villes
    CITIES.forEach(city => {
      if (city.toLowerCase().includes(query)) {
        results.push({
          type: 'location',
          text: city,
          subtext: 'Tous les artisans',
          icon: MapPin,
          color: '#3B82F6'
        });
      }
    });
    
    // 4. Combinaisons métier + ville
    if (query.includes(' ')) {
      const parts = query.split(' ');
      const matchedMetier = metiers.find(m => m.toLowerCase().includes(parts[0]));
      const matchedCity = CITIES.find(c => c.toLowerCase().includes(parts[parts.length - 1]));
      
      if (matchedMetier && matchedCity) {
        results.unshift({
          type: 'combo',
          text: `${matchedMetier} ${matchedCity}`,
          subtext: `${matchedMetier}s à ${matchedCity}`,
          icon: MapPin,
          color: '#8B5CF6'
        });
      }
    }
    
    return results.slice(0, 6);
  }, [localQuery]);
  
  // Filtrer les artisans correspondants
  useEffect(() => {
    if (!localQuery || localQuery.length < 2) {
      setMatchedArtisans([]);
      return;
    }
    
    const query = localQuery.toLowerCase();
    
    // Trouver les métiers correspondants via intentions
    let targetMetiers = [];
    for (const [intent, metiers] of Object.entries(INTENT_MAPPING)) {
      if (intent.includes(query) || query.includes(intent)) {
        targetMetiers = [...targetMetiers, ...metiers];
      }
    }
    
    const filtered = artisans.filter(a => {
      const metierMatch = a.metier?.toLowerCase().includes(query) || 
                         targetMetiers.some(m => a.metier?.toLowerCase().includes(m.toLowerCase()));
      const nameMatch = a.shop_name?.toLowerCase().includes(query);
      const cityMatch = a.city?.toLowerCase().includes(query);
      return metierMatch || nameMatch || cityMatch;
    });
    
    // Tri intelligent: vérifié > note > avis
    const sorted = filtered.sort((a, b) => {
      if (a.is_verified && !b.is_verified) return -1;
      if (!a.is_verified && b.is_verified) return 1;
      if ((a.rating_average || 0) !== (b.rating_average || 0)) {
        return (b.rating_average || 0) - (a.rating_average || 0);
      }
      return (b.reviews_count || 0) - (a.reviews_count || 0);
    });
    
    setMatchedArtisans(sorted.slice(0, 4));
  }, [localQuery, artisans]);
  
  // Mettre à jour les suggestions
  useEffect(() => {
    setSuggestions(generateSuggestions);
  }, [generateSuggestions]);
  
  const handleSuggestionClick = (suggestion) => {
    const searchText = suggestion.text;
    setLocalQuery(searchText);
    if (setSearchQuery) setSearchQuery(searchText);
    
    // Extraire métier et ville si présents
    const metiers = ['Plombier', 'Électricien', 'Peintre', 'Maçon', 'Carreleur', 
                     'Menuisier', 'Couvreur', 'Chauffagiste', 'Pisciniste', 'Paysagiste',
                     'Architecte', 'Géomètre', 'Expert bâtiment', 'Bureau d\'études', 
                     'Diagnostiqueur', 'Maître d\'œuvre'];
    const matchedMetier = metiers.find(m => searchText.toLowerCase().includes(m.toLowerCase()));
    const matchedCity = CITIES.find(c => searchText.toLowerCase().includes(c.toLowerCase()));
    
    if (onSearch) {
      onSearch({
        query: searchText,
        metier: matchedMetier || null,
        city: matchedCity || null
      });
    }
    onClose();
  };
  
  const handleArtisanClick = (artisan) => {
    // Navigate to artisan profile (route is /artisans/:shopId)
    window.location.href = `/artisans/${artisan.seller_id || artisan.id}`;
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    if (onSearch && localQuery.trim()) {
      onSearch({ query: localQuery.trim() });
    }
    onClose();
  };
  
  // Badges dynamiques pour les artisans
  const getArtisanBadges = (artisan) => {
    const badges = [];
    if (artisan.is_verified) {
      badges.push({ icon: Shield, text: 'Vérifié', color: '#10B981' });
    }
    if (artisan.rating_average >= 4.5) {
      badges.push({ icon: Award, text: 'Top noté', color: '#F59E0B' });
    }
    if (artisan.reviews_count > 50) {
      badges.push({ icon: TrendingUp, text: 'Très demandé', color: '#EF4444' });
    }
    return badges.slice(0, 2);
  };
  
  if (!isOpen) return null;
  
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        transition={{ duration: 0.15 }}
        className="absolute left-0 right-0 top-full mt-2 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden z-[100]"
        style={{ 
          maxHeight: '70vh',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0,0,0,0.05)'
        }}
        data-testid="smart-search-dropdown"
      >
        {/* Header avec recherche */}
        <div className="sticky top-0 bg-white border-b border-gray-100 p-4">
          <form onSubmit={handleSubmit} className="relative">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              ref={inputRef}
              type="text"
              value={localQuery}
              onChange={(e) => setLocalQuery(e.target.value)}
              placeholder="Rechercher un artisan, un métier, une ville..."
              className="w-full h-12 pl-12 pr-12 bg-gray-50 rounded-xl text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#F97316]/30 focus:bg-white transition-all"
              autoComplete="off"
            />
            {localQuery && (
              <button
                type="button"
                onClick={() => setLocalQuery('')}
                className="absolute right-4 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-200 rounded-full transition-colors"
              >
                <X size={16} className="text-gray-400" />
              </button>
            )}
          </form>
        </div>
        
        <div className="overflow-y-auto" style={{ maxHeight: 'calc(70vh - 80px)' }}>
          {/* Section: Suggestions intelligentes */}
          {localQuery.length >= 2 && suggestions.length > 0 && (
            <div className="p-4 border-b border-gray-50">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                <Sparkles size={12} />
                Suggestions
              </p>
              <div className="space-y-1">
                {suggestions.map((s, idx) => (
                  <button
                    key={`suggestion-${idx}`}
                    onClick={() => handleSuggestionClick(s)}
                    className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors text-left group"
                  >
                    <div 
                      className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-105"
                      style={{ backgroundColor: `${s.color}15` }}
                    >
                      <s.icon size={18} style={{ color: s.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">{s.text}</p>
                      <p className="text-xs text-gray-500 truncate">{s.subtext}</p>
                    </div>
                    <ChevronRight size={16} className="text-gray-300 group-hover:text-gray-500 transition-colors" />
                  </button>
                ))}
              </div>
            </div>
          )}
          
          {/* Section: Artisans disponibles (résultats directs) */}
          {matchedArtisans.length > 0 && (
            <div className="p-4 border-b border-gray-50">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                <Users size={12} />
                Artisans disponibles
              </p>
              <div className="space-y-2">
                {matchedArtisans.map((artisan, idx) => {
                  const badges = getArtisanBadges(artisan);
                  return (
                    <button
                      key={`artisan-${artisan.id || idx}`}
                      onClick={() => handleArtisanClick(artisan)}
                      className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gradient-to-r hover:from-orange-50 hover:to-amber-50 transition-all text-left group border border-transparent hover:border-orange-100"
                    >
                      {/* Photo */}
                      <div className="relative flex-shrink-0">
                        <img
                          src={artisan.logo || `https://ui-avatars.com/api/?name=${encodeURIComponent(artisan.shop_name || 'A')}&background=F97316&color=fff&size=48`}
                          alt={artisan.shop_name}
                          className="w-12 h-12 rounded-xl object-cover"
                        />
                        {artisan.is_verified && (
                          <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center border-2 border-white">
                            <Shield size={10} className="text-white" />
                          </div>
                        )}
                      </div>
                      
                      {/* Infos */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-gray-900 truncate">{artisan.shop_name}</p>
                          {badges.slice(0, 1).map((badge, i) => (
                            <span 
                              key={i}
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium"
                              style={{ backgroundColor: `${badge.color}15`, color: badge.color }}
                            >
                              <badge.icon size={10} />
                              {badge.text}
                            </span>
                          ))}
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-xs text-[#F97316] font-medium">{artisan.metier}</span>
                          <span className="text-gray-300">•</span>
                          <span className="text-xs text-gray-500 flex items-center gap-1">
                            <MapPin size={10} />
                            {artisan.city}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="flex items-center gap-1 text-xs">
                            <Star size={12} className="text-amber-400 fill-amber-400" />
                            <span className="font-semibold text-gray-900">{artisan.rating_average?.toFixed(1) || '4.5'}</span>
                            <span className="text-gray-400">({artisan.reviews_count || 0})</span>
                          </span>
                          <span className="inline-flex items-center gap-1 text-xs text-emerald-600 font-medium">
                            <Zap size={10} />
                            Disponible
                          </span>
                        </div>
                      </div>
                      
                      <ChevronRight size={18} className="text-gray-300 group-hover:text-[#F97316] transition-colors" />
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          
          {/* Section: Recherches populaires (si pas de query) */}
          {localQuery.length < 2 && (
            <div className="p-4 border-b border-gray-50">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                <TrendingUp size={12} />
                Recherches populaires
              </p>
              <div className="flex flex-wrap gap-2">
                {POPULAR_SEARCHES.map((search, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      setLocalQuery(search.text);
                      handleSuggestionClick({ text: search.text, type: 'popular' });
                    }}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gray-50 hover:bg-gray-100 transition-colors text-sm font-medium text-gray-700"
                  >
                    <search.icon size={14} style={{ color: search.color }} />
                    {search.text}
                  </button>
                ))}
              </div>
            </div>
          )}
          
          {/* Bouton Filtres avancés */}
          {onOpenFilters && (
            <div className="p-4">
              <button
                onClick={() => {
                  onClose();
                  setTimeout(() => onOpenFilters(), 100);
                }}
                className="w-full flex items-center justify-center gap-3 py-4 rounded-xl font-semibold text-white transition-all hover:shadow-lg active:scale-[0.98]"
                style={{
                  background: 'linear-gradient(135deg, #1E3A5F 0%, #2D4A6F 100%)',
                  boxShadow: '0 4px 14px rgba(30, 58, 95, 0.25)',
                }}
                data-testid="smart-search-open-filters"
              >
                <SlidersHorizontal size={18} />
                <span>Trouve ton artisan en 3 clics</span>
              </button>
            </div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
